import React, { useRef, useState, useEffect } from "react";
import ReactDOM from "react-dom";
import SignatureCanvas from "react-signature-canvas";
import { getImageDataUrlFromServer } from "../../utils/EmbedFields";

export default function AutoSignature({
  visible,
  fieldId,
  onSave,
  onCancel,
  initialDataUrl,
}) {
  const sigRef = useRef(null);
  const [penColor, setPenColor] = useState("black");
  const [hasDrawn, setHasDrawn] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!visible || !initialDataUrl || !sigRef.current) return;

    (async () => {
      let uri = initialDataUrl;

      if (!uri.startsWith("data:")) {
        setLoading(true);
        try {
          uri = await getImageDataUrlFromServer(uri);
        } catch (err) {
          console.error("couldn't preload signature:", err);
          setLoading(false);
          return;
        }
      }

      sigRef.current.fromDataURL(uri);
      setHasDrawn(false);
      setLoading(false);
    })();
  }, [visible, initialDataUrl]);

  if (!visible) return null;

  const handleClear = () => {
    sigRef.current.clear();
    setHasDrawn(false);
  };

  const handleEndStroke = () => {
    setHasDrawn(true);
  };

  const colors = ["black", "blue", "red"];
  const colorClasses = {
    black: "bg-black",
    blue: "bg-blue-500",
    red: "bg-red-500",
  };

  return ReactDOM.createPortal(
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-tier-primary dark:bg-tier-primary-dark dark:border-gray-600 p-6 rounded-lg shadow-lg w-full max-w-lg border">
        {/* Color picker */}
        <div className="flex space-x-2 mb-4">
          {colors.map((c) => (
            <button
              key={c}
              onClick={() => setPenColor(c)}
              className={[
                "w-8 h-8 rounded-full flex items-center justify-center transition",
                penColor === c
                  ? "ring-2 ring-blue-400 shadow-lg"
                  : "border-2 border-gray-300",
                colorClasses[c],
              ].join(" ")}
            >
              {penColor === c && (
                <span className="material-symbols-outlined text-white text-sm">
                  check
                </span>
              )}
            </button>
          ))}
        </div>

        <div className="relative">
          <SignatureCanvas
            ref={sigRef}
            penColor={penColor}
            onEnd={handleEndStroke}
            canvasProps={{
              className:
                "border border-gray-300 bg-gray-100 rounded-lg w-full h-48",
            }}
          />

          {loading && (
            <div className="absolute inset-0 bg-white/70 flex flex-col items-center justify-center z-20 rounded-lg">
              <span className="material-symbols-outlined animate-spin">
                progress_activity
              </span>
              <div className="text-gray-600 font-medium">
                Loading signature...
              </div>
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="mt-4 flex justify-end space-x-2">
          <button onClick={handleClear} className="btn-action-white">
            Clear
          </button>
          <button
            className="btn-action"
            onClick={() => {
              const canvas = sigRef.current.getCanvas();
              const dataUrl = canvas.toDataURL("image/png");
              onSave(fieldId, dataUrl);
            }}
            disabled={!hasDrawn}
            style={{
              opacity: hasDrawn ? 1 : 0.5,
              cursor: hasDrawn ? "pointer" : "not-allowed",
            }}
          >
            Save
          </button>
          <button onClick={onCancel} className="btn-action-white">
            Cancel
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
